// src/utils/security/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect } from "react";
import { loginUser, revokeToken } from "../../services/authService";
import { jwtDecode } from 'jwt-decode';


// Define the shape of your JWT payload (adjust keys as needed)
interface JWTPayload {
  sub: string;   // typically the username
  role: string;  // user role (and any other claims, e.g., group)
  // For example, if your token carries a 'group' claim you can add:
  // group?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean; // Indicates if authentication status is being determined
  errorMessage: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  user?: JWTPayload | null; // New: store the decoded user info from token
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loading: true, // Initialize as true since we're checking auth status on mount
  errorMessage: null,
  login: async () => {},
  logout: async () => {},
  user: null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Manage loading state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [user, setUser] = useState<JWTPayload | null>(null); // Store decoded token info

  // On mount, check for an existing access token in localStorage.
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode<JWTPayload>(token);
        ////console.log("User info from token on mount:", decoded);
        setUser(decoded);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Error decoding token on mount:", error);
      }
    }
    setLoading(false); // Authentication check complete
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setErrorMessage(null);
      const { accessToken, refreshToken } = await loginUser(username, password);

      // Store both tokens in localStorage
      localStorage.setItem("authToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      setIsAuthenticated(true);

      // Decode token and log user info
      const decoded = jwtDecode<JWTPayload>(accessToken);
      setUser(decoded);
    } catch (err) {
      console.error("Login error:", err);
      setIsAuthenticated(false);
      if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage("Login failed, please try again.");
      }
    }
  };

  const logout = async () => {
    const accessToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");
  
    // Attempt to revoke both tokens
    try {
      if (accessToken) {
        await revokeToken(accessToken);
      }
      if (refreshToken) {
        await revokeToken(refreshToken);
      }
    } catch (error) {
      console.error("Error revoking tokens:", error);
    } finally {
      // Clear tokens from localStorage and update auth state
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      setIsAuthenticated(false);
      setUser(null);
    }
  };  

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loading,
        errorMessage,
        login,
        logout,
        user, 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Helper hook to access auth context
export const useAuth = () => useContext(AuthContext);
